'use client';

import { useEffect, useState } from 'react';
import { useCountUp } from 'use-count-up';

/**
 * [DC01] Atomic component for animated number counting with optional formatting
 */
interface CountUpDisplayProps {
  value: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  className?: string;
  onComplete?: () => void;
  thousandsSeparator?: string;
  decimalSeparator?: string;
}

// Named export for better tree-shaking and explicit imports
export function CountUpDisplay({
  value,
  duration = 2,
  prefix = '',
  suffix = '',
  className = '',
  onComplete,
  thousandsSeparator = '.',
  decimalSeparator = ','
}: CountUpDisplayProps) {
  // [DM05] Use functional approach with state
  const [isMounted, setIsMounted] = useState(false);
  
  // [CP01] Simple animation using a library rather than complex custom code
  const { value: displayValue } = useCountUp({
    isCounting: isMounted,
    start: 0,
    end: value,
    duration,
    thousandsSeparator,
    decimalSeparator,
    onComplete,
  });
  
  // [CG01] Handle lifecycle
  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);
  
  // [CP03] Early return if not mounted
  if (!isMounted) return null;

  return (
    <div className={className}>
      {prefix}{displayValue}{suffix}
    </div>
  );
}
